import * as React from "react";
import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MuiToolbar from "@mui/material/Toolbar";
import { tabsClasses } from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MenuOpenRoundedIcon from "@mui/icons-material/MenuOpenRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import SideMenuMobile from "./SideMenuMobile";
import MenuButton from "./MenuButton";
import logo from "../components/Images/paras&roadlines.jpg";
import { useSidebar } from "./SidebarContext";

const Toolbar = styled(MuiToolbar)({
  width: "100%",
  padding: "10px 16px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexShrink: 0,
  minHeight: "64px !important",
  [`& ${tabsClasses.flexContainer}`]: {
    gap: "8px",
    p: "8px",
    pb: 0,
  },
});

// ─── SEARCH BAR ──────────────────────────────────────────────────
const SearchWrap = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #F1F5F9",
  transition: "all .2s ease",
  "&:hover, &:focus-within": {
    borderColor: "#FECACA",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 0 0 3px rgba(220,38,38,.08)",
  },
  width: "100%",
  maxWidth: 340,
  display: "flex",
  alignItems: "center",
}));

const SearchIconBox = styled("div")({
  padding: "0 10px",
  height: "100%",
  display: "flex",
  alignItems: "center",
  color: "#94A3B8",
});

const StyledInputBase = styled(InputBase)({
  color: "#0F172A",
  width: "100%",
  fontSize: "0.85rem",
  "& .MuiInputBase-input": {
    padding: "8px 10px 8px 0",
    width: "100%",
  },
});

// ─── DATE PILL ──────────────────────────────────────────────────
const DatePill = ({ compact = false }) => {
  const now = new Date();
  const day = now.getDate().toString().padStart(2, "0");
  const month = now.toLocaleString("en-GB", { month: "short" });
  const year = now.getFullYear();
  const weekday = now.toLocaleString("en-GB", { weekday: "long" });

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: "10px",
        border: "1px solid #E7EAF0",
        bgcolor: "#fff",
        minWidth: compact ? "auto" : 130,
      }}
    >
      <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: "#4F46E5" }} />
      <Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "#0F172A",
            lineHeight: 1.1,
          }}
        >
          {day} {month} {year}
        </Typography>
        {!compact && (
          <Typography
            sx={{ fontSize: "0.65rem", color: "#64748B", lineHeight: 1.1 }}
          >
            {weekday}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

// ─── USER CHIP ──────────────────────────────────────────────────
const UserChip = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const userName = localStorage.getItem("name")?.toUpperCase() || "USER";
  const initial = userName.charAt(0);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: "10px",
          border: "1px solid #E7EAF0",
          bgcolor: "#fff",
          cursor: "pointer",
          transition: "all .2s ease",
          "&:hover": { borderColor: "#C7D2FE", bgcolor: "#F5F6FB" },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: "0.85rem",
            fontWeight: 700,
            background: "linear-gradient(135deg,#4F46E5 0%,#7C3AED 100%)",
            color: "#fff",
          }}
        >
          {initial}
        </Avatar>
        <Box sx={{ minWidth: 0, display: { xs: "none", sm: "block" } }}>
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#0F172A",
              lineHeight: 1.1,
            }}
          >
            {userName}
          </Typography>
          <Typography
            sx={{ fontSize: "0.65rem", color: "#64748B", lineHeight: 1.1 }}
          >
            Administrator
          </Typography>
        </Box>
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: "#64748B" }} />
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "10px",
            border: "1px solid #E7EAF0",
            boxShadow: "0 8px 24px rgba(16,24,40,.08)",
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => setAnchorEl(null)}
          sx={{ fontSize: "0.85rem" }}
        >
          <ListItemIcon>
            <PersonRoundedIcon sx={{ fontSize: 18, color: "#4F46E5" }} />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => setAnchorEl(null)}
          sx={{ fontSize: "0.85rem" }}
        >
          <ListItemIcon>
            <SettingsRoundedIcon sx={{ fontSize: 18, color: "#64748B" }} />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{ fontSize: "0.85rem", color: "#DC2626" }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon sx={{ fontSize: 18, color: "#DC2626" }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export function DesktopTopBar() {
  const { open, toggle } = useSidebar();

  return (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2.5,
        py: 1.25,
        bgcolor: "#fff",
        borderBottom: "1px solid #E7EAF0",
        width: "100%",
        minHeight: 64,
        height: 64,
        flexShrink: 0,
        boxShadow: "0 1px 3px rgba(16,24,40,.04)",
        zIndex: 10,
      }}
    >
      {/* Left: Hamburger + Search */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ flex: 1, minWidth: 0 }}
      >
        <IconButton
          onClick={toggle}
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            color: "#334155",
            bgcolor: "#FFFFFF",
            border: "1px solid #F1F5F9",
            flexShrink: 0,
            transition: "all .2s ease",
            "&:hover": {
              bgcolor: "#FEF2F2",
              borderColor: "#FECACA",
              color: "#DC2626",
            },
          }}
        >
          {open ? (
            <MenuOpenRoundedIcon sx={{ fontSize: 22 }} />
          ) : (
            <MenuRoundedIcon sx={{ fontSize: 22 }} />
          )}
        </IconButton>

        <Box sx={{ flex: 1, maxWidth: 380 }}>
          <SearchWrap>
            <SearchIconBox>
              <SearchIcon sx={{ fontSize: 18 }} />
            </SearchIconBox>
            <StyledInputBase
              placeholder="Search here..."
              inputProps={{ "aria-label": "search" }}
            />
          </SearchWrap>
        </Box>
      </Stack>

      {/* Right: Notifications + Date + User */}
      <Stack direction="row" spacing={1.25} alignItems="center">
        <IconButton
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "1px solid #E7EAF0",
            bgcolor: "#fff",
            "&:hover": { bgcolor: "#F5F6FB", borderColor: "#C7D2FE" },
          }}
        >
          <Badge
            badgeContent={3}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.6rem",
                minWidth: 16,
                height: 16,
                fontWeight: 700,
              },
            }}
          >
            <NotificationsNoneRoundedIcon
              sx={{ fontSize: 20, color: "#334155" }}
            />
          </Badge>
        </IconButton>

        <DatePill />
        <UserChip />
      </Stack>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════
// MOBILE APP BAR (Existing)
// ═══════════════════════════════════════════════════════════════
export default function AppNavbar() {
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        display: { xs: "flex", md: "none" },
        backgroundColor: "#fff",
        backgroundImage: "none",
        opacity: 1,
        backdropFilter: "none",
        boxShadow: "0 2px 12px rgba(16,24,40,.06)",
        borderBottom: "1px solid #E7EAF0",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
      }}
    >
      <Toolbar variant="regular">
        {/* Left: Logo */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <div className="logo-icon">
            <img src={logo} alt="P" className="logo-img" />
          </div>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#4F46E5",
                fontWeight: 800,
                fontSize: "1.05rem",
                letterSpacing: "0.5px",
                lineHeight: 1.1,
              }}
            >
              PARAS
            </Typography>
            <Typography
              sx={{
                color: "#64748B",
                fontSize: "0.65rem",
                letterSpacing: "1px",
                fontWeight: 600,
              }}
            >
              ROAD LINES
            </Typography>
          </Box>
        </Stack>

        {/* Right: Notifications + Menu */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: "#F5F6FB",
              "&:hover": { bgcolor: "#EEF2FF" },
            }}
          >
            <Badge
              badgeContent={3}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.55rem",
                  minWidth: 14,
                  height: 14,
                },
              }}
            >
              <NotificationsNoneRoundedIcon
                sx={{ fontSize: 18, color: "#334155" }}
              />
            </Badge>
          </IconButton>

          <MenuButton aria-label="menu" onClick={toggleDrawer(true)}>
            <MenuRoundedIcon sx={{ color: "#0F172A" }} />
          </MenuButton>
          <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
        </Stack>
      </Toolbar>
      <style>{`
        .logo-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: 8px;
        }
        .logo-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      `}</style>
    </AppBar>
  );
}

export function CustomIcon() {
  return (
    <Box
      sx={{
        width: "1.5rem",
        height: "1.5rem",
        bgcolor: "black",
        borderRadius: "999px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        backgroundImage:
          "linear-gradient(135deg, hsl(210, 98%, 60%) 0%, hsl(210, 100%, 35%) 100%)",
        color: "#fff",
        border: "1px solid",
        borderColor: "hsl(210, 100%, 55%)",
        boxShadow: "inset 0 2px 5px rgba(255, 255, 255, 0.3)",
      }}
    >
      <DashboardRoundedIcon color="inherit" sx={{ fontSize: "1rem" }} />
    </Box>
  );
}